import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { z } from 'zod';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AlertCircle } from 'lucide-react';

const authSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const promoCode = searchParams.get('promo');
  const initialTab = searchParams.get('tab') as 'signin' | 'signup' | null;
  const [mode, setMode] = useState<'signin' | 'signup'>(initialTab || 'signin');
  const [geoData, setGeoData] = useState<{ blocked: boolean; country: string; ip?: string } | null>(null);
  const [geoLoading, setGeoLoading] = useState(true);
  const [showGeoDialog, setShowGeoDialog] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate('/portfolio');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkGeo = async () => {
      try {
        const SUPABASE_URL = 'https://vrshqdsjmiexlewrzcbg.supabase.co';
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/geolocation-check`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setGeoData(data);
          
          // Show dialog if user is from RU/BY
          if (data.blocked) {
            setShowGeoDialog(true);
          }
        } else {
          // Fail-open: if API doesn't work, allow registration
          console.error('Geo API error:', response.status);
          setGeoData({ blocked: false, country: 'UNKNOWN' });
        }
      } catch (error) {
        console.error('Geo check error:', error);
        // Fail-open: on error allow registration
        setGeoData({ blocked: false, country: 'ERROR' });
      } finally {
        setGeoLoading(false);
      }
    };

    checkGeo();
  }, []);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check geolocation - ONLY for registration
    if (geoData?.blocked) {
      toast.error(t('geo.registrationBlocked'));
      setShowGeoDialog(true);
      return;
    }
    
    setLoading(true);

    try {
      const validation = authSchema.safeParse({ email, password });
      if (!validation.success) {
        toast.error(t('auth.invalidEmailFormat'));
        return;
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast.error(t('auth.userAlreadyExists'));
        } else {
          toast.error(error.message);
        }
        return;
      }

      toast.success(t('auth.accountCreated'));
      
      // If there's a promo code for Pro trial, activate it
      if (promoCode === 'pro_trial') {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: subError } = await supabase
            .from('subscriptions')
            .update({
              plan: 'pro',
              status: 'trial',
              trial_ends_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              current_period_start: new Date().toISOString(),
              current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            })
            .eq('user_id', user.id);
          
          if (!subError) {
            toast.success(t('auth.proTrialActivated'));
          }
        }
      }
      
      navigate('/portfolio');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validation = authSchema.safeParse({ email, password });
      if (!validation.success) {
        toast.error(t('auth.invalidEmailFormat'));
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error(t('auth.invalidCredentials'));
        } else {
          toast.error(error.message);
        }
        return;
      }

      navigate('/portfolio');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <Helmet>
        <link rel="canonical" href="https://rental.shift-box.com/auth" />
        <link rel="alternate" hrefLang="x-default" href="https://rental.shift-box.com/auth" />
      </Helmet>
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Rental Manager</h1>
          <p className="mt-2 text-muted-foreground">
            {mode === 'signin' ? t('auth.signIn') : t('auth.createAccount')}
          </p>
        </div>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'signin' | 'signup')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
            <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={handleSignIn} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="signin-email">{t('auth.email')}</Label>
                <Input
                  id="signin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterEmail')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signin-password">{t('auth.password')}</Label>
                <Input
                  id="signin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.enterPassword')}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t('common.loading') : t('auth.signInWithPassword')}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignUp} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="signup-email">{t('auth.email')}</Label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterEmail')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">{t('auth.password')}</Label>
                <Input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.enterPassword')}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t('auth.passwordMinLength')}
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || geoLoading || (geoData?.blocked || false)}
              >
                {loading ? t('common.loading') : t('auth.createAccount')}
              </Button>

              {geoData?.blocked && !geoLoading && (
                <p className="text-xs text-destructive text-center mt-2 flex items-center justify-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {t('geo.registrationNotAvailable')}
                </p>
              )}

              <p className="text-xs text-muted-foreground text-center mt-4">
                {t('auth.agreeToTerms')}{' '}
                <button
                  type="button"
                  onClick={() => navigate('/privacy')}
                  className="underline hover:text-foreground"
                >
                  {t('auth.privacyPolicy')}
                </button>
              </p>
            </form>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={showGeoDialog} onOpenChange={setShowGeoDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t('geo.notAvailableTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-2">
              <p>{t('geo.notAvailableMessage')}</p>
              <p className="text-xs text-muted-foreground mt-4">
                {t('geo.notAvailableFooter')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowGeoDialog(false)}>
              {t('common.understood')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Auth;
